/**
 * Per-user token consumption, ordered by total tokens descending. Same
 * table shape as /admin/people, plus the pager controls that page lacks
 * (usage rows grow unbounded; 25-per-page without navigation would hide
 * everyone below the top spenders).
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatTokensExact, useUserUsage } from './hooks';
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
export function UserUsageTable({ range }) {
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const { data, isLoading } = useUserUsage(range, search, page, PAGE_SIZE);
    // Debounce the query the RPC sees — without this every keystroke
    // round-trips browser → sabaody → shanks → pangaea (review finding).
    useEffect(()=>{
        const timer = setTimeout(()=>setSearch(searchInput), SEARCH_DEBOUNCE_MS);
        return ()=>clearTimeout(timer);
    }, [
        searchInput
    ]);
    const users = data?.users ?? [];
    const total = data?.total ?? 0;
    const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-base font-semibold",
                        children: "Usage by user"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        "data-testid": Testids.adminUsage.searchInput,
                        placeholder: "Search by name or email",
                        value: searchInput,
                        onChange: (e)=>{
                            setSearchInput(e.target.value);
                            setPage(1);
                        },
                        className: "max-w-sm"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Table, {
                "data-testid": Testids.adminUsage.table,
                children: [
                    /*#__PURE__*/ _jsx(TableHeader, {
                        children: /*#__PURE__*/ _jsxs(TableRow, {
                            children: [
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Name"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Email"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    className: "text-right",
                                    children: "Prompt"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    className: "text-right",
                                    children: "Completion"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    className: "text-right",
                                    children: "Total"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TableBody, {
                        children: [
                            users.map((u)=>/*#__PURE__*/ _jsxs(TableRow, {
                                    "data-testid": Testids.adminUsage.row,
                                    "data-row-id": u.userId,
                                    children: [
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "font-medium",
                                            children: u.name || u.email || /*#__PURE__*/ _jsx("span", {
                                                className: "font-mono text-xs",
                                                children: u.userId
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "text-muted-foreground",
                                            children: u.email
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "text-right tabular-nums",
                                            children: formatTokensExact(u.totals?.promptTokens)
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "text-right tabular-nums",
                                            children: formatTokensExact(u.totals?.completionTokens)
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "text-right font-medium tabular-nums",
                                            children: formatTokensExact(u.totals?.totalTokens)
                                        })
                                    ]
                                }, u.userId)),
                            !isLoading && users.length === 0 && /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: 5,
                                    className: "text-center text-muted-foreground",
                                    children: "No usage recorded in this range"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm text-muted-foreground tabular-nums",
                        "data-testid": Testids.adminUsage.pagerLabel,
                        children: [
                            from,
                            "–",
                            to,
                            " of ",
                            total
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        "data-testid": Testids.adminUsage.pagerPrev,
                        variant: "outline",
                        size: "icon",
                        disabled: page <= 1,
                        onClick: ()=>setPage((p)=>Math.max(1, p - 1)),
                        children: /*#__PURE__*/ _jsx(ChevronLeft, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        "data-testid": Testids.adminUsage.pagerNext,
                        variant: "outline",
                        size: "icon",
                        disabled: to >= total,
                        onClick: ()=>setPage((p)=>p + 1),
                        children: /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            })
        ]
    });
}
